import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { cartService } from '@/features/cart/services/cartService';
import type { CartResponse } from '@/features/cart/types';
import CartCard from '@/features/cart/components/CartCard';
import CartCardSkeleton from '@/components/skeleton/CartCardSkeleton';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const AllCartsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isGuest, isLoading: authLoading, user } = useAuth();
  
  const [carts, setCarts] = useState<CartResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCarts = React.useCallback(async () => {
    // Wait for auth check to complete
    if (authLoading) return;
    
    // If not authenticated, show empty state instead of redirecting
    if (!isAuthenticated) {
      setLoading(false);
      setCarts([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await cartService.getAllCarts();
      console.log('Fetched carts:', data);
      
      // Sort carts by expires_at (newest first)
      const sortedCarts = (data || []).sort((a, b) => 
        new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime()
      );
      
      setCarts(sortedCarts);
      // Note: Don't refetch cart count here - it will be fetched from localStorage or API by useCartCount hook
    } catch (err) {
      console.error('Failed to fetch carts:', err);
      // If 401, show empty state instead of redirecting
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number }; status?: number };
        if (axiosError?.response?.status === 401 || axiosError?.status === 401) {
          setCarts([]);
          setLoading(false);
          return;
        }
      }
      setError('Failed to load your carts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    // Fetch carts on mount - but only if authenticated
    fetchCarts();
  }, [fetchCarts]);

  const handleDeleteCart = async (cartId: string) => {
    try {
      await cartService.abandonCart(cartId);
      setCarts(carts.filter(cart => cart.id !== cartId));
      // Cart count will auto-refresh via header's stale-while-revalidate
    } catch (err) {
      console.error('Failed to delete cart:', err);
      setError('Failed to delete cart. Please try again.');
      throw err; // Re-throw to let the modal handle it
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-black pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-12 pt-12">
            <div className="relative flex items-center justify-center mb-4">
              <h1 className="text-4xl font-bold text-white">
                Your Carts
              </h1>
              {!authLoading && isAuthenticated && !isGuest && user && !user.is_guest && (
                <button
                  onClick={() => navigate('/profile?view=purchases')}
                  className="absolute right-0 inline-flex items-center gap-2 px-4 py-2 text-blue-400 hover:text-blue-300 border border-blue-500/30 hover:border-blue-400/50 rounded-lg transition-all text-sm font-medium bg-blue-500/20 hover:bg-blue-500/30 shadow-md shadow-black/20 hover:shadow-lg hover:shadow-black/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  Previous Purchases
                </button>
              )}
            </div>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto text-center">
              Manage your active shopping carts
            </p>
          </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <CartCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && carts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg
              className="w-24 h-24 text-gray-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="text-2xl font-semibold text-white mb-2">No Carts Yet</h2>
            <p className="text-gray-400 mb-6 max-w-md">
              You don't have any active shopping carts. Start browsing events and add items to your cart!
            </p>
            <button
              onClick={() => navigate('/events')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Browse Events
            </button>
          </div>
        )}

        {/* Carts List */}
        {!loading && carts.length > 0 && (
          <div className="space-y-6">
            {carts.map((cart) => (
              <CartCard
                key={cart.id}
                cart={cart}
                onDelete={handleDeleteCart}
              />
            ))}
          </div>
        )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AllCartsPage;


